import { React, useState, useEffect, useContext } from "react";
import "./viewCharts.css";
import { parseData } from "../../helpers/twxDataParser";
import { getAggregatedData, getDataByTimeRange } from "../../services/service";
import { DateContext } from "../navigation/navigationDrawer";
import {
  Chart,
  Series,
  Tooltip,
  CommonSeriesSettings,
  ZoomAndPan
} from "devextreme-react/chart";
import DataGrid, { Column, Paging, Pager } from "devextreme-react/data-grid";
import PivotGrid, { FieldChooser } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import PieChart, {
  Legend,
  Label,
  Connector
} from "devextreme-react/pie-chart";
import { Series as PieSeries } from "devextreme-react/pie-chart";
const ViewCharts = ({
  gridKey,
  chartDataModelObjects,
  machineValues,
  propertyValues,
  isDataLoadingByDate,
  setIsDataLoadingByDate
}) => {
  const chartCategories = [
    "Line",
    "Bar",
    "Side By Side Bar",
    "Table",
    "Pivot Table",
    "Doughnut Chart",
  ];
  const { startDate, endDate, selectedDate } = useContext(DateContext);
  const dimensions = ["Date", "Machines"];
  const aggregationTypes = ["Sum", "Average", "Count"];
  const [chartType, setChartType] = useState();
  const [selectedDimension, setSelectedDimension] = useState(dimensions[0]);
  const [aggregationType] = useState(aggregationTypes[0]);
  const [chartData, setChartData] = useState();
  const [selectedMachine, setSelectedMachine] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState([]);
  const [chartKeysList, setChartKeysList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [pivotData, setPivotData] = useState(new PivotGridDataSource());
  const [sideBySideData, setSideBySideData] = useState([]);
  const [measureType, setMeasureType] = useState("timestamp");

  const getTWXDataByDate = async (
    startDateValue,
    endDateValue,
    selectedMachineValue,
    selectedPropertyValue,
    aggregationType,
    dimensionType
  ) => {
    if (dimensionType === "Date") {
      let dataByDateObject = {
        startDate: startDateValue,
        endDate: endDateValue,
        machines: selectedMachineValue,
        properties: selectedPropertyValue,
        aggregationTime: selectedDate.text
      };
      return getDataByTimeRange(dataByDateObject);
    } else {
      let dataByDateObject = {
        startDate: startDateValue,
        endDate: endDateValue,
        machines: selectedMachineValue,
        properties: selectedPropertyValue,
        aggregationType,
        aggregationTime: selectedDate.text
      };
      return getAggregatedData(dataByDateObject);
    }
  };

  /**
   *  GET the data from thingworkx and parse it update the Machines and Property lables along with binding it with the charts data
   */
  const parseResponseData = async () => {
    try {
      setIsDataLoadingByDate(true);
      let selectedMachineValue = [machineValues[0].machine];
      let selectedPropertyValue = [propertyValues[0].property_name];
      let selectedChartTypeValue = chartCategories[0];
      let selectedDimensionValue = dimensions[0];

      const selectedChartModel = chartDataModelObjects.find(
        (item) => item.key === gridKey
      );

      if (selectedChartModel) {
        selectedMachineValue = selectedChartModel.machines;
        selectedPropertyValue = selectedChartModel.property;
        selectedChartTypeValue = selectedChartModel.chartType;
        selectedDimensionValue = selectedChartModel.dimensionType;
      }

      setChartType(selectedChartTypeValue);
      setSelectedMachine(selectedMachineValue);
      setSelectedProperty(selectedPropertyValue);
      setSelectedDimension(selectedDimensionValue);

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      getTWXDataByDate(
        formattedStartDate,
        formattedEndDate,
        selectedMachineValue,
        selectedPropertyValue,
        aggregationType,
        selectedDimensionValue
      ).then((twxData) => {
        setIsDataLoadingByDate(false);
        updateChartData(
          twxData,
          chartType,
          selectedDimensionValue,
          selectedPropertyValue,
          selectedMachineValue
        );
      });
    } catch (error) {
      setIsDataLoadingByDate(false);
      console.log(error);
    }
  };

  /**
   * updates the chart data parameter to make it into a format read by Chart.js
   */
  const updateChartData = (
    twxData,
    selectedChartType,
    selectedDimension,
    selectedProperties,
    selectedMachines
  ) => {
    const { chartDataSource, chartKeys } = parseData(
      twxData,
      selectedChartType,
      selectedDimension,
      selectedProperties,
      selectedMachines
    );
    setChartKeysList(chartKeys);
    setChartData(chartDataSource);
    if (chartDataSource.length > 0) {
      if (chartDataSource[0].timestamp) setMeasureType("timestamp");
      else setMeasureType("property");
    }
    setTableData(twxData);
    let dataSource = new PivotGridDataSource({
      fields: [
        {
          caption: "Machine",
          dataField: "machine",
          expanded: true,
          area: "row",
        },
        {
          caption: "Property",
          dataField: "property_name",
          expanded: true,
          area: "row",
        },
        {
          dataField: "time",
          dataType: "date",
          expanded: true,
          area: "column",
        },
        {
          caption: "Value",
          dataField: "property_value",
          dataType: "number",
          summaryType: "mean",
          area: "data",
        },
      ],
      store: twxData,
    });
    if (selectedDimension === "Machines") {
      dataSource = new PivotGridDataSource({
        fields: [
          {
            caption: "Machine",
            dataField: "machine",
            expanded: true,
            area: "row",
          },
          {
            caption: "Property",
            dataField: "property_name",
            expanded: true,
            area: "row",
          },
          {
            caption: "Value",
            dataField: "property_value",
            dataType: "number",
            summaryType: "mean",
            area: "data",
          },
        ],
        store: twxData,
      });
    }
    setPivotData(dataSource);
    if (
      chartDataSource.length > 0 &&
      selectedChartType === "Side By Side Bar"
    ) {
      setMeasureType("machine");
      const sideBySideDataArray = chartDataSource.map((item) => {
        const machine = selectedMachines.find((m) => item.property.includes(m));
        const propertyName = selectedProperties.find((p) =>
          item.property.includes(p)
        );
        return {
          machine: machine,
          [propertyName]: item[item.property],
        };
      });
      // Store sideBySideData in state
      setSideBySideData(sideBySideDataArray);
    } else {
      setSideBySideData([]);
    }
  };

  function customizeTooltip(arg) {
    return {
      text: `${arg.seriesName} | ${arg.value}`,
    };
  }

  useEffect(() => {
    parseResponseData();
  }, [chartDataModelObjects, startDate, endDate]);

  return (
    <div className="charts-container">
      {!isDataLoadingByDate && chartData != null ? (
        <div>
          <div className="chart-header">
            {selectedProperty.join(", ")} <br />
            <span className="selected-machine">
              {selectedMachine.join(", ")}
            </span>
          </div>
          <div
            key={gridKey}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {chartType === chartCategories[0] && (
              <Chart
                id="line-chart"
                key="line-chart"
                className="chart-template"
                dataSource={chartData}
                palette="Soft"
              >
                <ZoomAndPan argumentAxis="both" valueAxis="both" />
                {chartKeysList.map((item, index) => (
                  <Series
                    key={index}
                    valueField={item}
                    argumentField={measureType}
                    type="line"
                    name={item}
                  />
                ))}
                <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
                <Legend
                  visible={false}
                  verticalAlignment="bottom"
                  horizontalAlignment="center"
                  hoverMode="excludePoints"
                />
              </Chart>
            )}
            {chartType === chartCategories[1] && (
              <Chart
                id="bar-chart"
                className="chart-template"
                dataSource={chartData}
                palette="Soft"
              >
                <ZoomAndPan argumentAxis="both" valueAxis="both" />
                {chartKeysList.map((item, index) => (
                  <Series
                    key={index}
                    valueField={item}
                    argumentField={measureType}
                    type="bar"
                    name={item}
                  />
                ))}
                <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
                <Legend
                  visible={false}
                  verticalAlignment="bottom"
                  horizontalAlignment="center"
                  hoverMode="excludePoints"
                />
              </Chart>
            )}

            {chartType === chartCategories[2] && sideBySideData && (
              <Chart
                id="chart"
                className="chart-template"
                dataSource={sideBySideData}
              >
                <CommonSeriesSettings argumentField="machine" type="bar" />
                {selectedProperty.map((item, index) => (
                  <Series key={index} valueField={item} type="bar" />
                ))}
                <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
                <Legend
                  visible={false}
                  verticalAlignment="bottom"
                  horizontalAlignment="center"
                  hoverMode="excludePoints"
                />
              </Chart>
            )}

            {chartType === chartCategories[3] && (
              <DataGrid
                id="data-grid"
                className="chart-container"
                dataSource={tableData}
                showBorders={true}
              >
                <Column dataField="machine" dataType="string" />
                <Column dataField="property_name" dataType="string" />
                <Column dataField="property_value" dataType="number" />

                <Paging defaultPageSize={12} />
                <Pager
                  showPageSizeSelector={true}
                  allowedPageSizes={[8, 12, 20]}
                />
              </DataGrid>
            )}

            {chartType === chartCategories[4] && (
              <PivotGrid
                id="pivot-grid"
                width={"auto"}
                dataSource={pivotData}
                allowSortingBySummary={true}
                showColumnGrandTotals={false}
                showRowTotals={false}
                showRowGrandTotals={false}
                allowSorting={true}
                allowFiltering={true}
                allowExpandAll={true}
                showBorders={true}
              >
                <FieldChooser enabled={false} />
              </PivotGrid>
            )}

            {chartType === chartCategories[5] && (
              <PieChart
                id="pie"
                type="doughnut"
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                palette="Soft Pastel"
                dataSource={chartData}
              >
                <PieSeries valueField={"value"} argumentField={measureType}>
                  <Label visible={true}>
                    <Connector visible={true} />
                  </Label>
                </PieSeries>
                <Legend
                  visible={false}
                  margin={0}
                  horizontalAlignment="right"
                  verticalAlignment="top"
                />
              </PieChart>
            )}
          </div>
        </div>
      ) : (
        <p>loading...</p>
      )}
    </div>
  );
};

export default ViewCharts;
